import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MemberService } from '../../../core/members/member.service';
import { MemberTokenType } from '@app/shared/types/member_token_type';
import { first, map, publishLast, refCount, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { SUBUNIT_PARAM_NAME } from '@app/shared/const';
import { Settings } from '@app/core/_models/settings';
import { SettingsService } from '@app/core/settings/settings.service';
import { EmailUnsubscriptionService } from '@app/shared/views/unsubscription/services/email-unsubscription.service';
import { MsisdnUnsubscriptionService } from '@app/shared/views/unsubscription/services/msisdn-unsubscription.service';
import { HttpClient } from '@angular/common/http';
import { BaseParamsService } from '@app/core/base-params.service';
import { IdentifierType } from '@app/core/_models/member-interface';
import { UnsubscriptionBaseService } from '@app/shared/views/unsubscription/services/unsubscription.base.service';
import { TopNavbarComponent } from '@app/shared/components/top-navbar/top-navbar.component';
import { LoyaltyClubHeaderComponent } from '@app/shared/components/loyalty-club-header/loyalty-club-header.component';
import { TranslatePipe } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { UnsubscribeTranslationKeyWithIdentifierTypePipe } from '@app/shared/views/unsubscription/unsubscribe-translation-key-with-identifier-type.pipe';
import { TranslateCutPipe } from '@app/shared/views/unsubscription/translate-cut.pipe';
import { LoyaltyClubFooterComponent } from '@app/shared/components/loyalty-club-footer/loyalty-club-footer.component';
import { AlertComponent } from '@app/shared/components/alert/alert.component';

export const URL_MERGE_TAG_REGEX = /%\((\w+)\)/gm;

export interface SettingsData {
  name: string;
  loginEnabled: boolean;
  unsubscriptionRedirectionUrl: string;
}

const mapSettingsToViewModel = (settings: Settings): SettingsData => ({
  name: settings.getSubunitDisplayName(),
  loginEnabled: settings.login_enabled,
  unsubscriptionRedirectionUrl: settings.unsubscription_redirection_url,
});

@Component({
  selector: 'app-unsubscription',
  templateUrl: './unsubscription.component.html',
  imports: [
    TopNavbarComponent,
    LoyaltyClubHeaderComponent,
    TranslatePipe,
    AsyncPipe,
    ReactiveFormsModule,
    UnsubscribeTranslationKeyWithIdentifierTypePipe,
    TranslateCutPipe,
    LoyaltyClubFooterComponent,
    RouterLink,
    AlertComponent,
  ],
})
export class UnsubscriptionComponent implements OnDestroy {
  public form = this.formBuilder.group({
    isOptout: false,
  });

  public isTokenCorrect = false;
  public isUnsubscribed = false;
  public isError = false;
  public identifier: string | null;
  public identifierType: IdentifierType;

  public readonly settings$: Observable<SettingsData> = this.getSettings().pipe(
    publishLast(),
    refCount(),
  );

  private unsubscriptionRedirectionUrl: string | null;
  private token: string | null;
  private subject: string | null;
  private campaignId: string | null;
  private boostcomId: string | null;
  private subunitId: string | null;

  private unsubscribe$ = new Subject();

  private unsubscriptionService: UnsubscriptionBaseService;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private formBuilder: FormBuilder,
              private memberService: MemberService,
              private settingsService: SettingsService,
              private http: HttpClient,
              private baseParamsService: BaseParamsService) {

    this.setIdentifierType();
    this.onQueryParamsReceived();
    this.setUnsubscriptionRedirectionUrl();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  protected navigateToMemberPage(): void {
    this.router.navigate(['../../profile'], { relativeTo: this.route, replaceUrl: true });
  }

  protected unsubscribe(): void {
    this.unsubscriptionService.unsubscribe(decodeURIComponent(this.identifier || ''), {
      token: this.token || '',
      subunitId: this.subunitId,
    })
      .subscribe({
          next: () => this.onUnsubscribeSuccess(),
          error: (response) => {
            if (response.error?.errors?.token) {
              this.isTokenCorrect = false;
            } else {
              this.isError = true;
            }
          },
        },
      );
  }

  private onUnsubscribeSuccess(): void {
    const unsubscriptionRedirectionUrl = this.unsubscriptionRedirectionUrl;

    if (unsubscriptionRedirectionUrl) {
      this.redirectAfterUnsubscribe(unsubscriptionRedirectionUrl);
    } else {
      this.isUnsubscribed = true;
    }
  }

  private redirectAfterUnsubscribe(rawUrl: string): void {
    window.location.href = this.mergeUrlValues(rawUrl);
  }

  private mergeUrlValues(url: string): string {
    const mergeTags = url.match(URL_MERGE_TAG_REGEX);

    if (!mergeTags) { return url; }

    const mergeValues = {
      '%(subject)': this.subject,
      '%(boostcomid)': this.boostcomId,
      '%(email)': window.btoa(this.identifier || ''),
      '%(msisdn)': window.btoa(this.identifier || ''),
      '%(campaign_id)': this.campaignId,
    };

    mergeTags.forEach(tag => {
      url = url.replace(tag, encodeURIComponent(mergeValues[tag] || ''));
    });

    return url;
  }

  private onQueryParamsReceived(): void {
    this.route.queryParamMap.pipe(
      first(),
    ).subscribe(params => this.assignQueryParamsValuesToVariables(params));
  }

  private assignQueryParamsValuesToVariables(paramMap: ParamMap): void {
    const email = paramMap.get('email');
    const msisdn = paramMap.get('msisdn');
    this.identifier = email || msisdn;
    this.token = paramMap.get('token');
    this.subject = paramMap.get('subject');
    this.campaignId = paramMap.get('campaign_id');
    this.boostcomId = paramMap.get('boostcomid');
    this.subunitId = paramMap.get(SUBUNIT_PARAM_NAME);

    this.unsubscriptionService = this.createUnsubscriptionService();

    this.verifyToken();
  }

  private createUnsubscriptionService(): UnsubscriptionBaseService {
    return this.identifierType === IdentifierType.EMAIL
      ? new EmailUnsubscriptionService(this.http, this.baseParamsService)
      : new MsisdnUnsubscriptionService(this.http, this.baseParamsService);
  }

  private verifyToken(): void {
    this.memberService.verifyToken(
      this.identifier || '',
      MemberTokenType.Unsubscription,
      this.token || '',
      this.identifierType,
    ).subscribe(
      (isValid) => {
        this.isTokenCorrect = isValid;
      },
      () => this.isTokenCorrect = false,
    );
  }

  private getSettings(): Observable<SettingsData> {
    return this.settingsService.getSettingsOnce()
      .pipe(
        map(mapSettingsToViewModel),
      );
  }

  private setUnsubscriptionRedirectionUrl(): void {
    this.settings$.pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(settings => this.unsubscriptionRedirectionUrl = settings.unsubscriptionRedirectionUrl);
  }

  private setIdentifierType(): void {
    this.identifierType = this.route.snapshot.data.identifier;
  }
}
